import { useEffect, useState } from 'react';
import { ScreenFrame } from '@/components/Screen';
import { SectionCard } from '@/components/DataList';
import { Icon } from '@/components/icons';
import { settings } from '@/api/endpoints';
import { errorMessage, pickList, type JsonMap } from '@/api/client';
import { Button, Empty, ErrorBox, FlashMessage, Input, Loading, StatusBadge } from '@/components/ui';
import { formatDateTime, str } from '@/utils/format';

type ApiSyriaState = {
  has_token: boolean;
  last_synced_at?: unknown;
  accounts: JsonMap[];
};

export function UssdScreen() {
  const [data, setData] = useState<ApiSyriaState>({ has_token: false, accounts: [] });
  const [apiKey, setApiKey] = useState('');
  const [pins, setPins] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await settings.apiSyria();
      if (!res.success) {
        setError(errorMessage(res, 'تعذر تحميل إعدادات API Syria.'));
        setData({ has_token: false, accounts: [] });
        setPins({});
        return;
      }
      const payload =
        res.data && typeof res.data === 'object' && !Array.isArray(res.data)
          ? (res.data as JsonMap)
          : (res as JsonMap);
      const accounts = Array.isArray(payload.accounts)
        ? (payload.accounts as JsonMap[])
        : pickList(payload, 'accounts', 'items', 'data');
      setData({
        has_token: Boolean(payload.has_token),
        last_synced_at: payload.last_synced_at,
        accounts,
      });
      const nextPins: Record<string, string> = {};
      accounts.forEach((acc) => {
        if (acc.pin_code) nextPins[str(acc.id)] = str(acc.pin_code);
      });
      setPins(nextPins);
    } catch {
      setError('تعذر تحميل إعدادات API Syria. تحقق من الاتصال ثم حاول مرة أخرى.');
      setData({ has_token: false, accounts: [] });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function saveToken() {
    if (!apiKey.trim()) return;
    setBusy(true);
    const res = await settings.saveApiSyriaToken({ api_key: apiKey.trim() });
    setBusy(false);
    setMsg(errorMessage(res, res.success ? 'تم حفظ المفتاح.' : 'تعذر الحفظ.'));
    if (res.success) {
      setApiKey('');
      void load();
    }
  }

  async function syncAccounts() {
    setBusy(true);
    const res = await settings.syncAccounts();
    setBusy(false);
    setMsg(errorMessage(res, res.success ? 'تم جلب الحسابات.' : 'تعذر الجلب.'));
    if (res.success) void load();
  }

  async function savePin(accountId: unknown) {
    const id = str(accountId);
    setBusy(true);
    const res = await settings.savePin({
      account_id: accountId,
      pin_code: pins[id] || '',
    });
    setBusy(false);
    setMsg(errorMessage(res, res.success ? 'تم حفظ PIN.' : 'تعذر حفظ PIN.'));
    if (res.success) {
      const saved = str((res.data as JsonMap | undefined)?.pin_code, pins[id] || '');
      if (saved) setPins((prev) => ({ ...prev, [id]: saved }));
      void load();
    }
  }

  async function toggleAccount(account: JsonMap) {
    const next = Number(account.is_active) === 1 ? 0 : 1;
    setBusy(true);
    const res = await settings.toggleActive({
      account_id: account.id,
      is_active: next,
    });
    setBusy(false);
    setMsg(errorMessage(res, res.success ? 'تم تحديث الحساب.' : 'تعذر التحديث.'));
    if (res.success) void load();
  }

  if (loading) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={() => void load()} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="settings"
      title="إعدادات API Syria"
      subtitle="إدارة مفتاح API Syria وحسابات التنفيذ الخاصة به فقط. إعدادات أجهزة وشرائح USSD موجودة في صفحة منفذ USSD."
      actions={
        <Button type="button" variant="secondary" onClick={() => void load()}>
          <Icon name="refresh" size={18} /> تحديث
        </Button>
      }
    >
      <FlashMessage message={msg} />

      <SectionCard
        title="إعدادات API SYRIA"
        subtitle="احفظ مفتاح الربط ثم اجلب حسابات API Syria المطلوبة للتنفيذ."
        icon={<Icon name="settings" size={24} />}
      >
        <div className="form-grid">
          <Input
            label="مفتاح API"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="X-Api-Key"
          />
          <div className="action-row wrap">
            <Button type="button" disabled={busy || !apiKey.trim()} onClick={() => void saveToken()}>
              حفظ المفتاح
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={busy || !data.has_token}
              onClick={() => void syncAccounts()}
            >
              جلب الحسابات
            </Button>
          </div>
          {data.has_token ? (
            <small className="muted">
              المفتاح محفوظ
              {data.last_synced_at ? ` · آخر مزامنة: ${formatDateTime(data.last_synced_at)}` : ''}
            </small>
          ) : (
            <small className="muted">لا يوجد مفتاح محفوظ بعد.</small>
          )}
        </div>

        <div className="list-stack top-space">
          {data.accounts.length === 0 ? (
            <Empty title="لا توجد حسابات" hint="احفظ مفتاح API ثم اجلب الحسابات." />
          ) : (
            data.accounts.map((acc) => {
              const active = Number(acc.is_active) === 1;
              const hasPin = Boolean(acc.has_pin) || Boolean(str(acc.pin_code, ''));
              const status = active ? (hasPin ? 'approved' : 'pending') : 'blocked';
              const statusLabel = active
                ? hasPin
                  ? 'PIN محفوظ'
                  : 'PIN مطلوب'
                : 'الحساب معطل';
              return (
                <article key={str(acc.id)} className="list-card compact">
                  <div className="list-head">
                    <strong dir="ltr">{str(acc.gsm)}</strong>
                    <span className={`status-pill ${status}`}>{statusLabel}</span>
                  </div>
                  {acc.cash_code && str(acc.cash_code) !== str(acc.gsm) ? (
                    <small>كود الكاش: {str(acc.cash_code)}</small>
                  ) : null}
                  <div className="form-grid two-columns">
                    <Input
                      label="PIN ظاهر بعد الحفظ"
                      value={pins[str(acc.id)] || ''}
                      onChange={(e) =>
                        setPins((prev) => ({ ...prev, [str(acc.id)]: e.target.value }))
                      }
                      inputMode="numeric"
                      placeholder={hasPin ? 'PIN محفوظ' : 'أدخل PIN'}
                    />
                    <Button
                      type="button"
                      className="align-end"
                      disabled={busy}
                      onClick={() => void savePin(acc.id)}
                    >
                      حفظ PIN
                    </Button>
                  </div>
                  <div className="action-row wrap top-space">
                    <Button
                      type="button"
                      variant={active ? 'danger' : 'secondary'}
                      disabled={busy}
                      onClick={() => void toggleAccount(acc)}
                    >
                      {active ? 'تعطيل الحساب' : 'تفعيل الحساب'}
                    </Button>
                    <StatusBadge status={status} />
                  </div>
                </article>
              );
            })
          )}
        </div>
      </SectionCard>
    </ScreenFrame>
  );
}
